import * as React from 'react';

import { type PaletteDef, typeQueryInPalette, usePaletteHandle } from '@gitbook/react-uikit';
import { TextRange } from '@gitbook/slate';
import type { MarkProps } from '@gitbook/slate-react';
import { useLiveRef, useTimeout } from '@gitbook/util-react-hooks';

/**
 * Component to render around a temporary mark, for example when typing ":" or "@".
 */
export function SelectionPalettePicker<PickerMarkData extends { value: string }>(
    props: MarkProps & {
        getPalette: (markData: PickerMarkData, getCurrentRange: () => TextRange) => PaletteDef;
    }
): React.ReactElement {
    const { mark, node, offset, attributes, children, editor, getPalette } = props;

    const markData = mark.data.toObject<PickerMarkData>();
    const { value } = markData;

    const anchorRef = React.useRef<HTMLSpanElement>(null);
    const lastestValueRef = useLiveRef(value);
    const paletteHandle = usePaletteHandle(anchorRef, {
        onClose: () => {
            editor.change((change) => change.focus());
        },
    });

    useTimeout(() => {
        /**
         * Open the palette only if the editor is focused otherwise if the editor re-renders
         * and the latest selection is around the "@" mark, the palette will be opened unexpectedly.
         */
        if (!paletteHandle.opened && editor.value.isFocused) {
            const getCurrentRange = () =>
                TextRange.create({
                    anchorKey: node.key,
                    anchorOffset: offset,
                    focusKey: node.key,
                    focusOffset: offset + lastestValueRef.current.length + 1,
                });

            paletteHandle.open(typeQueryInPalette(getPalette(markData, getCurrentRange), value));
        }
    }, 150);

    // When the user types in the editor, we filter the mentions
    React.useEffect(() => {
        if (paletteHandle.opened) {
            paletteHandle.typeQuery(value);
        }
    }, [paletteHandle, value]);

    return (
        <span ref={anchorRef} {...attributes}>
            {children}
        </span>
    );
}
